import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useAlerts } from "contexts";
import {
  useCancelableDebouncedApi,
  useEffectAfterMount,
  useQueryParam,
} from "hooks";
import { useParams } from "react-router";
import { isValidValue, logger, setParamsKeyName } from "utils";
import useSelection from "./useSelection";
import { useSelector } from "react-redux";
import { selectDistrictData } from "store/slices";
import i18n from "i18n";

/**
 * Scrolls the first MUI TableContainer to the top smoothly.
 */
const scrollToTop = () => {
  const container = document.getElementsByClassName("MuiTableContainer-root");
  if (container?.length) {
    container[0].scrollTo({ top: 0, behavior: "smooth" });
  }
};

/**
 * Core hook to fetch, manage, and paginate data for a table or list.
 * Includes multi-select management, infinite scrolling, and error handling.
 *
 * @param {Object} options
 * @param {string} options.pageName - Identifier for the page / data type
 * @param {Function} options.fetchFn - Function to fetch data from API
 * @param {boolean} options.append - Whether to append new data or replace (default: true)
 * @param {Function} options.deleteFn - Function to delete a row/item
 * @param {number} options.limit - Items per page (default: 10)
 *
 * @returns {Object} Utilities and state for a paginated data table
 */
// What one row of the list is called in alerts ("Tag deleted successfully")
const rowItemName = (pageName) => i18n.t(`common:rowItemNames.${pageName}`, { defaultValue: pageName });

const useDataCore = ({
  pageName,
  fetchFn,
  fetchByidFn,
  append = true,
  isTableView = true,
  deleteFn,
  restoreFn,
  limit = 10,
  defaultParams = null,
  showLoader = true,
  isCallFetchFn = true,
}) => {
  // Debounced API call with cancellation
  const { callApi, cancelApi } = useCancelableDebouncedApi();

  // React-router params and search params
  const { campusId, domainId } = useParams();
  const [pages, setPage] = useQueryParam(
    setParamsKeyName("page", isTableView),
    "1",
  );
  const [id, setId] = useQueryParam("id", null);
  const [status] = useQueryParam(
    "status",
    pageName === "domainLE" ? "" : "active",
  );
  const [type] = useQueryParam("type", "");
  const [search] = useQueryParam(setParamsKeyName("search", isTableView), "");

  const { id: districtId } = useSelector(selectDistrictData);
  // Global alerts and dialogs
  const { showAlerts } = useAlerts();

  // Multi-select state
  const { selected, setSelected, handleItemClick, handleSelectAll } =
    useSelection();

  // Data table state
  const [customParams, setCustomParams] = useState(
    !!defaultParams ? JSON.parse(defaultParams) : {},
  );
  const [isFetchApi, setIsFetchApi] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [callData, setCallData] = useState(false);

  // Extract search and status parameters
  // const search = searchParams.get("search") || "";
  // const status = searchParams.get("status") || "active";
  const page = useMemo(() => parseInt(pages), [pages]);

  /**
   * Build API params for fetchFn.
   * useMemo ensures stable object reference unless dependencies change.
   */
  const params = useMemo(() => {
    return {
      limit,
      domain_id: domainId || undefined,
      campus_id: campusId || undefined,
      district_id: districtId || undefined,
      search,
      page,
      ...(pageName === "domain" && {
        is_active: !!status ? status === "active" : true,
      }),
      ...(pageName === "domainLE" &&
        !!status && { is_active: status === "active" }),
      ...(status === "archived" && { archived: true }),
      ...(status === "pending" && { unassigned_internal: true }),
      ...(status === "invite" && { invited: true }),
      ...(pageName === "ExternalAdmin" ||
      pageName === "Staff" ||
      pageName === "Law Enforcement" ||
      pageName === "Student"
        ? { role: pageName }
        : {}),
      ...(pageName === "allUsers"
        ? { role: ["Staff", "Student", "Law Enforcement"] }
        : {}),
      ...customParams,
    };
  }, [
    limit,
    domainId,
    campusId,
    districtId,
    search,
    page,
    pageName,
    status,
    customParams,
  ]);

  /**
   * Fetch data from API
   * @param {boolean} overrideAppend - Append data (true) or replace data (false)
   */
  const getData = useCallback(
    async (overrideAppend = append) => {
      setIsLoading(true);
      try {
        await callApi(
          async ({ signal }) => {
            const res = await fetchFn({ params, config: { signal } });

            if (!res.error) {
              // Append or replace data and delete duplicate entries
              setData((prev) => {
                const newData = overrideAppend
                  ? [...prev, ...res.rowData]
                  : res.rowData;

                // Deduplicate by `id`, keeping the latest item
                const uniqueData = Array.from(
                  new Map(newData.map((item) => [item.id, item])).values(),
                );

                return uniqueData;
              });
              setHasError(false);

              const total = res.data?.meta?.total ?? res.rowData.length ?? 0;
              setCount(total);
              // Determine if more data exists for infinite scrolling
              if (total > 0) {
                setHasMore(
                  (prevPage) =>
                    (params.page - 1) * params.limit + res.rowData.length <
                    total,
                );
              } else {
                setHasMore(res.rowData.length === params.limit);
              }

              // Scroll to top and reset selection if replacing
              if (!overrideAppend) {
                scrollToTop();
                setSelected([]);
              }
            } else {
              if (!overrideAppend) {
                setData([]);
                setCount(0);
              }
              setHasError(true);
              setHasMore(false);
            }
            return res;
          },
          { showLoader },
        );
      } catch (error) {
        if (!axios.isCancel(error)) {
          logger.error(error);
          if (!overrideAppend) {
            setData([]);
            setCount(0);
          }
          setHasError(true);
          setHasMore(false);
        }
      } finally {
        setIsLoading(false);
        setIsFetchApi(true);
      }
    },
    [fetchFn, callApi, params, showLoader, append, setSelected],
  );
  const getLatestDataList = useCallback(async () => {
    // Paged table (not infinite scroll): reload only the page on screen.
    // Reloading pages 1..N in one request would show page × limit rows, and a
    // page emptied by a delete steps back one page
    if (!append) {
      setIsLoading(true);
      try {
        await callApi(
          async ({ signal }) => {
            const res = await fetchFn({ params, config: { signal } });
            if (!res.error) {
              if (!res.rowData.length && params.page > 1) {
                setPage(params.page - 1, { replace: true });
                return res;
              }
              setData(res.rowData);
              setCount(res.data?.meta?.total ?? res.rowData.length ?? 0);
              setHasError(false);
              setSelected([]);
            }
            return res;
          },
          { showLoader: false },
        );
      } catch (error) {
        if (!axios.isCancel(error)) logger.error(error);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    setIsLoading(true);
    try {
      const { limit, page, ...rest } = params;
      const newLimit = page * limit;
      await callApi(
        async ({ signal }) => {
          const res = await fetchFn({
            params: { ...rest, page: 1, limit: newLimit },
            config: { signal },
          });

          if (!res.error) {
            // Append or replace data and delete duplicate entries
            setData((prev) => {
              const newData = res.rowData;

              // Deduplicate by `id`, keeping the latest item
              const uniqueData = Array.from(
                new Map(newData.map((item) => [item.id, item])).values(),
              );

              return uniqueData;
            });
            setHasError(false);

            const total = res.data?.meta?.total ?? res.rowData.length ?? 0;
            setCount(total);
            // Determine if more data exists for infinite scrolling
            if (total > 0) {
              setHasMore((prevPage) => res.rowData.length < total);
            } else {
              setHasMore(res.rowData.length === newLimit);
            }

            // Scroll to top and reset selection if replacing
            scrollToTop();
            setSelected([]);
          } else {
            setData([]);
            setCount(0);
            setHasError(true);
            setHasMore(false);
          }
          return res;
        },
        { showLoader:false },
      );
    } catch (error) {
      if (!axios.isCancel(error)) {
        logger.error(error);
        setData([]);
        setCount(0);
        setHasError(true);
        setHasMore(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchFn, callApi, params, setSelected, append, setPage]);

  /**
   * Refresh data by forcing a full reset
   */
  const refresh = useCallback(
    (isReset = false) => {
      setSelected([]);
      setHasError(false);
      if (isReset) {
        setData([]);
        setCount(0);
        setPage({ page: 1, search: null }, { replace: true });
      } else {
        setCallData((prev) => !prev);
      }
    },
    [setPage, setCallData, setData, setHasError, setCount, setSelected],
  );

  /**
   * Delete a single row/item
   * Updates local data state and shows an alert if successful
   */
  const deleteRow = useCallback(
    async ({ id, isPermanent = null, obj = {}, rowId }) => {
      try {
        await callApi(async () => {
          let data = {
            ...obj,
          };
          if (isValidValue(isPermanent)) {
            data["is_permanent_delete"] = isPermanent;
          }
          const res = await deleteFn(id, data);
          if (!res.error) {
            setCount((prev) => prev - 1);
            if (!append) {
              refresh();
            } else {
              setData((prev) => prev.filter((d) => d.id !== rowId));
              setSelected((prev) => prev.filter((e) => e !== rowId));
            }
            // isPermanent false = archive ("Staff member archived successfully")
            showAlerts(
              i18n.t(isPermanent === false ? "common:itemArchived" : "common:itemDeleted", {
                name: rowItemName(pageName),
              }),
            );
          }
          return res;
        });
      } catch (error) {
        logger.error(error);
      }
    },
    [
      deleteFn,
      pageName,
      showAlerts,
      refresh,
      append,
      callApi,
      setSelected,
      setCount,
    ],
  );
  const restoreRow = useCallback(
    async (id) => {
      try {
        await callApi(async () => {
          const res = await restoreFn(id);
          if (!res.error) {
            setCount((prev) => prev - 1);
            if (!append) {
              refresh();
            } else {
              setData((prev) => prev.filter((d) => d.id !== id));
              setSelected((prev) => prev.filter((e) => e !== id));
            }
            showAlerts(i18n.t("common:itemActivated", { name: rowItemName(pageName) }));
          }
          return res;
        });
      } catch (error) {
        logger.error(error);
      }
    },
    [
      restoreFn,
      pageName,
      showAlerts,
      refresh,
      append,
      callApi,
      setSelected,
      setCount,
    ],
  );

  const setCustomParamObj = useCallback(
    (obj) => {
      setCustomParams((prev) => ({ ...prev, ...obj }));
    },
    [setCustomParams],
  );

  /**
   * Load next page for infinite scrolling
   */
  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) return;
    // Loading starts now, not when the debounced fetch runs: until then
    // another loadMore (e.g. a re-created scroll observer) must not ask for
    // the page after this one
    setIsLoading(true);
    // Scrolling is not navigation: no history entry per page
    setPage(page + 1, { replace: true });
  }, [isLoading, hasMore, setPage, page]);

  const getDataById = useCallback(
    async (id) => {
      if (!!fetchByidFn) {
        try {
          await callApi(
            async ({ signal }) => {
              const res = await fetchByidFn(id, { signal });

              if (!res.error) {
                // replace data by id
                setData((prev) =>
                  prev.map((e) => (e.id === id ? res.data : e)),
                );
              }
              return res;
            },
            { showLoader: false },
          );
        } catch (error) {
          if (!axios.isCancel(error)) {
            logger.error(error);
          }
        }
      }
    },
    [fetchByidFn, callApi, setData],
  );
  useEffect(() => {
    if (!!id) {
      if (id === "add") {
        if (status === "invite") {
          refresh();
        }
      } else {
        getDataById(id);
      }
      setId(null);
    }
  }, [id, status, getDataById, refresh, setId]);

  useEffectAfterMount(() => {
    setData([]);
    setCount(0);
  }, [status, type, search]);

  /**
   * Handle API call on search, status, or page change
   * Debounced by 300ms
   */
  useEffect(() => {
    if (!isCallFetchFn) {
      return;
    }
    const timeout = setTimeout(() => {
      getData(append && params.page > 1);
    }, 300);

    return () => clearTimeout(timeout);
    // Refetch when the query (params), the explicit refetch trigger
    // (callData), the mode or the list itself (fetchFn, e.g. the 911
    // Dispatcher's Allowed / Rejected tabs) changes. getData is recreated
    // with those, so it is not listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, callData, isCallFetchFn, append, fetchFn]);

  /**
   * Cleanup on unmount: cancel any pending API calls
   */
  useEffect(() => {
    return () => cancelApi();
  }, [cancelApi]);

  // New default filters replace the old ones (Past -> Ongoing drops the
  // Past status filter); filters set by the page itself stay
  const lastDefaults = useRef(defaultParams);
  useEffect(() => {
    if (lastDefaults.current === defaultParams) return;
    const oldDefaults = lastDefaults.current ? JSON.parse(lastDefaults.current) : {};
    lastDefaults.current = defaultParams;
    const newDefaults = defaultParams ? JSON.parse(defaultParams) : {};
    setCustomParams((prev) => {
      const updated = { ...prev };
      Object.keys(oldDefaults).forEach((key) => delete updated[key]);
      Object.entries(newDefaults).forEach(([key, value]) => {
        if (!!value) updated[key] = value;
      });
      return updated;
    });
  }, [defaultParams]);

  useEffect(() => {
    setIsFetchApi(false);
  }, [status, search, type]);

  // A new filter (district picker, Ongoing / Past, ...) starts the list over:
  // the old filter's rows go and page 1 loads, instead of the next page of
  // the new filter being appended to the old rows
  const filterKey = JSON.stringify(customParams);
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    setData([]);
    setCount(0);
    setSelected([]);
    if (page > 1) setPage(1, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on a filter change
  }, [filterKey]);

  return {
    data,
    count,
    hasMore,
    isLoading,
    hasError,
    isFetchApi,

    getLatestDataList,
    getData,
    getDataById,
    refresh,
    loadMore,
    deleteRow,
    restoreRow,
    setData,
    setCount,
    setCustomParamObj,

    // selection
    selected,
    handleClick: handleItemClick,
    clearSelectedItem: () => handleSelectAll(false, []),
    handleSelectAllClick: (event) =>
      handleSelectAll(
        event.target.checked,
        data.map((d) => d.id),
      ),
  };
};

export default useDataCore;
